import { useState } from "react";
import PhotoLightbox from "./PhotoLightbox";

/**
 * Responsive masonry gallery.
 * - Mobile: single column, one photo per row.
 * - Tablet: 2 columns; Desktop (lg): 3 columns; XL: 4 columns.
 * - Uses CSS `columns` so every photo keeps its natural aspect ratio
 *   (portrait, landscape, square) and different-shaped photos tile cleanly
 *   without cropping.
 * - Photos are never cropped, stretched or zoomed — `object-fit` is not used.
 * - Captions and tap-to-enlarge lightbox preserved.
 */
export default function MorePhotosGallery({ photos, animalName }) {
  const [lightbox, setLightbox] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const items = (photos || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (items.length === 0) {
    return (
      <p className="font-body text-sm text-[#6D5A50] italic text-center">
        More photos of {animalName} coming soon.
      </p>
    );
  }

  return (
    <>
      <div
        data-testid="more-photos-grid"
        className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3 sm:gap-4 [column-fill:_balance]"
      >
        {items.map((p, i) => {
          const full = p.url.startsWith("http") ? p.url : `${backendUrl}${p.url}`;
          return (
            <button
              key={p.id || i}
              type="button"
              onClick={() => setLightbox(i)}
              className="group relative block w-full mb-3 sm:mb-4 break-inside-avoid overflow-hidden rounded-md bg-[#F5EFE1] border border-[#A87149]/30 page-shadow transition-transform hover:-translate-y-[2px]"
              aria-label={`Open photo ${i + 1} of ${items.length}`}
              data-testid={`gallery-thumb-${i}`}
            >
              <img
                src={full}
                alt={p.caption || `${animalName} photo ${i + 1}`}
                loading="lazy"
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
              />
              {p.caption && (
                <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs px-2 py-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.caption}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {lightbox !== null && (
        <PhotoLightbox
          items={items}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}
    </>
  );
}
